import { useEffect } from 'react';

export interface PageMetaOptions {
  title: string;
  description: string;
  canonical?: string;
  schema?: Record<string, any>;
  robots?: string; // e.g., "noindex, nofollow"
  ogImage?: string; // Open Graph image URL
  ogType?: string; // e.g., "article", "website"
  twitterCard?: string; // e.g., "summary_large_image"
  keywords?: string; // SEO keywords
  author?: string; // Article author
  publishedTime?: string; // ISO date string
}

/**
 * Hook to update page meta tags and schema markup dynamically
 * Useful for SEO and AdSense crawlability
 */
export function usePageMeta(options: PageMetaOptions) {
  useEffect(() => {
    // Update document title
    const originalTitle = document.title;
    document.title = options.title;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    const originalDescription = metaDescription?.getAttribute('content') || '';
    
    if (metaDescription) {
      metaDescription.setAttribute('content', options.description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', options.description);
      document.head.appendChild(metaDescription);
    }

    // Update or create robots meta tag
    let metaRobots: HTMLMetaElement | null = null;
    const originalRobots = document.querySelector('meta[name="robots"]')?.getAttribute('content') || '';
    
    if (options.robots) {
      metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.setAttribute('content', options.robots);
      } else {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        metaRobots.setAttribute('content', options.robots);
        document.head.appendChild(metaRobots);
      }
    }

    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    const originalCanonical = canonicalLink?.href || '';
    
    if (options.canonical) {
      if (canonicalLink) {
        canonicalLink.href = options.canonical;
      } else {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        canonicalLink.href = options.canonical;
        document.head.appendChild(canonicalLink);
      }
    }

    // Add schema markup if provided
    let schemaScript: HTMLScriptElement | null = null;
    if (options.schema) {
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.id = 'page-schema';
      schemaScript.textContent = JSON.stringify(options.schema);
      document.head.appendChild(schemaScript);
    }

    // Helper function to set or create meta tag
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(attr, property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
      return tag;
    };

    // Open Graph tags
    const ogTags: HTMLMetaElement[] = [];
    if (options.canonical) {
      ogTags.push(setMetaTag('og:url', options.canonical, true));
    }
    ogTags.push(setMetaTag('og:type', options.ogType || 'website', true));
    ogTags.push(setMetaTag('og:title', options.title, true));
    ogTags.push(setMetaTag('og:description', options.description, true));
    if (options.ogImage) {
      ogTags.push(setMetaTag('og:image', options.ogImage, true));
      ogTags.push(setMetaTag('og:image:alt', options.title, true));
    }
    ogTags.push(setMetaTag('og:site_name', "Let's Be Real", true));
    ogTags.push(setMetaTag('og:locale', 'en_AU', true));

    // Twitter Card tags
    const twitterTags: HTMLMetaElement[] = [];
    twitterTags.push(setMetaTag('twitter:card', options.twitterCard || 'summary_large_image'));
    twitterTags.push(setMetaTag('twitter:title', options.title));
    twitterTags.push(setMetaTag('twitter:description', options.description));
    if (options.ogImage) {
      twitterTags.push(setMetaTag('twitter:image', options.ogImage));
    }

    // Additional SEO meta tags
    const seoTags: HTMLMetaElement[] = [];
    if (options.keywords) {
      seoTags.push(setMetaTag('keywords', options.keywords));
    }
    if (options.author) {
      seoTags.push(setMetaTag('author', options.author));
    }
    if (options.publishedTime) {
      seoTags.push(setMetaTag('article:published_time', options.publishedTime, true));
    }

    // Language and region
    seoTags.push(setMetaTag('language', 'English'));
    seoTags.push(setMetaTag('geo.region', 'AU'));
    seoTags.push(setMetaTag('geo.placename', 'Australia'));

    // Cleanup function to restore original values
    return () => {
      document.title = originalTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', originalDescription);
      }
      if (metaRobots) {
        if (originalRobots) {
          metaRobots.setAttribute('content', originalRobots);
        } else {
          document.head.removeChild(metaRobots);
        }
      }
      if (canonicalLink && originalCanonical) {
        canonicalLink.href = originalCanonical;
      }
      if (schemaScript) {
        document.head.removeChild(schemaScript);
      }
      // Clean up OG, Twitter, and SEO tags
      [...ogTags, ...twitterTags, ...seoTags].forEach((tag) => {
        if (tag && tag.parentNode) {
          document.head.removeChild(tag);
        }
      });
    };
  }, [
    options.title,
    options.description,
    options.canonical,
    options.schema,
    options.robots,
    options.ogImage,
    options.ogType,
    options.twitterCard,
    options.keywords,
    options.author,
    options.publishedTime,
  ]);
}

/**
 * Helper to generate Article schema for sample reports
 */
export function createReportSchema(options: {
  title: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": options.title,
    "description": options.description,
    "url": options.url,
    "datePublished": options.datePublished || "2025-10-22",
    "dateModified": options.dateModified || "2025-10-22",
    "author": {
      "@type": "Organization",
      "name": "Let's Be Real"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Let's Be Real",
      "url": "https://letsbereal.com.au",
      "logo": {
        "@type": "ImageObject",
        "url": "https://letsbereal.com.au/favicon.svg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": options.url
    },
    "inLanguage": "en-AU"
  };
}

/**
 * Helper to generate Place schema for property reports
 */
export function createPropertyPlaceSchema(options: {
  address: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": options.address,
    "description": options.description,
    "url": options.url,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": options.address,
      "addressCountry": "AU"
    }
  };
}

